import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Square,
  Image,
  Layers,
  Music,
  Video,
  Palette,
  Settings,
  Sparkles,
  User
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

/* ── Compact profile avatar with credit ring ── */
const ProfileRingButton = ({ onClick }) => {
  const { user } = useAuth();
  const credits = user?.credits ?? 0;
  const creditsMax = 200;
  const percentage = Math.min(Math.max(credits / creditsMax, 0), 1);

  const size = 40;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - percentage);

  const photoUrl =
    user?.photo && user.photo !== 'default.jpg' ? user.photo : null;

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : null;

  return (
    <button
      onClick={onClick}
      className="relative w-10 h-10 flex-shrink-0 group"
      title="Account"
    >
      <svg
        width={size}
        height={size}
        className="absolute inset-0 -rotate-90 w-full h-full"
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#2B2B2B" strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#C6E982" strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>

      <div
        className="absolute rounded-full overflow-hidden bg-ag-black-600 flex items-center justify-center"
        style={{ inset: strokeWidth + 2 }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={user?.name || 'User'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className="w-full h-full flex items-center justify-center"
          style={{ display: photoUrl ? 'none' : 'flex' }}
        >
          {initials ? (
            <span className="text-[10px] font-semibold text-ag-green select-none leading-none">
              {initials}
            </span>
          ) : (
            <User size={14} className="text-ag-green" />
          )}
        </span>
      </div>
    </button>
  );
};

/* ── Popup that opens on profile click ── */
const ProfilePopup = ({ onClose }) => {
  const { user } = useAuth();
  const popupRef = useRef(null);
  const credits = user?.credits ?? 0;

  useEffect(() => {
    const handleClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={popupRef}
      className="absolute left-full bottom-0 ml-3 w-52 bg-ag-black-700 border border-ag-black-400 rounded-xl shadow-2xl p-3 z-[100] flex flex-col gap-2"
    >
      {/* User info */}
      <div className="flex items-center gap-2 pb-2 border-b border-ag-black-400">
        <div className="w-8 h-8 rounded-full bg-ag-black-600 flex items-center justify-center overflow-hidden flex-shrink-0">
          {user?.photo && user.photo !== 'default.jpg' ? (
            <img src={user.photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={14} className="text-ag-green" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-white font-medium truncate">{user?.name || 'User'}</p>
          <p className="text-[11px] text-ag-black-300 truncate">{user?.email || ''}</p>
        </div>
      </div>

      {/* Credits */}
      <div className="flex items-center justify-between py-1">
        <span className="text-xs text-ag-black-300">Credits left</span>
        <span className="text-xs font-semibold text-ag-green">{credits}</span>
      </div>

      {/* Upgrade */}
      <Link
        to="/manage-subscription"
        onClick={onClose}
        className="w-full text-center py-1.5 rounded-lg  border border-ag-green/40 text-ag-green text-xs font-semibold hover:bg-ag-green hover:text-ag-black-800 transition-colors duration-150"
      >
        Upgrade
      </Link>
    </div>
  );
};

/* ── Main sidebar ── */
const SidebarNavigation = ({ activeTab, onTabChange }) => {
  const [profileOpen, setProfileOpen] = useState(false);

  const navigationItems = [
    { id: 'uploads',   name: 'Uploads',   icon: Image,    tooltip: 'Your Uploads' },
    { id: 'templates', name: 'Templates', icon: Square,   tooltip: 'Pre-made Templates' },
    { id: 'elements',  name: 'Elements',  icon: Layers,   tooltip: 'Reusable Components' },
    { id: 'aiMedia',   name: 'AI Media',  icon: Sparkles, tooltip: 'AI Generated Media' }
  ];

  return (
    <div className="w-16 bg-ag-black-800 border-r border-ag-black-400 flex flex-col items-center py-4">
      {/* Nav items */}
      {navigationItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onTabChange(item.id)}
            className={`
              w-12 h-12 rounded-lg flex items-center justify-center mb-3
              relative group
              ${isActive
                ? 'bg-ag-black-800 border border-ag-black-400 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-ag-black-700'
              }
            `}
            title={item.tooltip}
          >
            <Icon size={20} />

            {/* Active indicator */}
            {isActive && (
              <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-ag-green rounded-l-full" />
            )}

            {/* Tooltip */}
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              {item.tooltip}
            </div>
          </button>
        );
      })}

      {/* Spacer pushes profile to bottom */}
      <div className="flex-1" />

      {/* Profile avatar with credit ring */}
      <div className="relative mb-2">
        <ProfileRingButton onClick={() => setProfileOpen((v) => !v)} />
        {profileOpen && <ProfilePopup onClose={() => setProfileOpen(false)} />}
      </div>
    </div>
  );
};

export default SidebarNavigation;